import { useMemo, useState } from 'react';
import { Activity, ChevronDown, HeartPulse, AlertTriangle, Info } from 'lucide-react';
import { cn } from '../ui/utils';
import { Skeleton } from '../ui/skeleton';
import { OrganScoreCard } from './OrganScoreCard';
import { OrganDetail } from './OrganDetail';
import {
  ORGAN_LABEL,
  ORGAN_ORDER,
  isInsufficient,
  unscorableGap,
  type OrganName,
  type OrganScore,
} from './organHealth';
import type { OrganScoreHistoryPoint } from '../../api/organHealthApi';

/** Fixed clinical order; "overall" is reserved for a later roll-up (shows "—"). */
function orderedOrgans(organs: OrganScore[]): OrganScore[] {
  const byName = new Map<OrganName, OrganScore>();
  for (const o of organs) byName.set(o.organ, o);
  return ORGAN_ORDER.map((name) => byName.get(name)).filter((o): o is OrganScore => Boolean(o));
}

function OverallReservedCard() {
  return (
    <div className="flex w-full flex-col gap-3 rounded border border-dashed border-border/60 bg-surface-sunken/10 p-4 text-left opacity-70">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-text-tertiary">Overall</span>
        <span className="rounded-md bg-surface-sunken px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
          Reserved
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-medium leading-none text-text-tertiary/60">—</span>
        <span className="text-sm font-medium text-text-tertiary/60">/100</span>
      </div>
      <span className="text-xs text-text-tertiary/70">Total-body roll-up coming later</span>
    </div>
  );
}

function PanelShell({ count, children }: { count?: number; children: React.ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="overflow-hidden rounded border border-border/50 bg-linear-to-br from-surface-sunken/20 via-background to-surface-sunken/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 px-4 py-3.5 text-left transition-colors hover:bg-surface-sunken/30"
      >
        <HeartPulse className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <span className="flex-1 text-sm font-medium text-foreground">Organ Health</span>
        {typeof count === 'number' && (
          <span className="text-xs tabular-nums text-text-tertiary">
            {count} organ{count !== 1 ? 's' : ''}
          </span>
        )}
        <ChevronDown className={cn('size-4 text-text-tertiary transition-transform duration-200', open && 'rotate-180')} />
      </button>
      {open && <div className="animate-in fade-in slide-in-from-top-1 px-4 pb-4 duration-200">{children}</div>}
    </div>
  );
}

/**
 * Clinician "Organ Panel": Kidney / Liver / Lung score cards (plus a reserved
 * Overall), with a detail view for the selected organ. Renders loading, error,
 * empty, and loaded states. Display-only — all scoring is done server-side.
 */
export function OrganHealthPanel({
  organs,
  loading = false,
  error,
  patientLabel,
  history,
}: {
  organs?: OrganScore[];
  loading?: boolean;
  error?: string | null;
  patientLabel?: string;
  /** Score history per organ, for the card sparklines. Omitted organs simply
   * render without a sparkline -- never blocks the score itself. */
  history?: Partial<Record<OrganName, OrganScoreHistoryPoint[]>>;
}) {
  const ordered = useMemo(() => orderedOrgans(organs ?? []), [organs]);
  const overall = useMemo(() => (organs ?? []).find((o) => o.organ === 'overall') ?? null, [organs]);
  const [selectedName, setSelectedName] = useState<OrganName | null>(null);

  const selected = useMemo(() => {
    const selectable = overall ? [...ordered, overall] : ordered;
    if (selectedName) return selectable.find((o) => o.organ === selectedName) ?? null;
    // Default: the first gradable organ, else the first present one.
    return ordered.find((o) => !isInsufficient(o)) ?? ordered[0] ?? null;
  }, [ordered, overall, selectedName]);

  if (loading) {
    return (
      <PanelShell>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded" />
          ))}
        </div>
      </PanelShell>
    );
  }

  if (error) {
    return (
      <PanelShell>
        <div className="flex items-center gap-2 rounded border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
          <span>Couldn’t load organ health scores. {error}</span>
        </div>
      </PanelShell>
    );
  }

  if (ordered.length === 0) {
    return (
      <PanelShell count={0}>
        <div className="flex flex-col items-center gap-2 rounded border border-dashed border-border/60 bg-surface-sunken/10 px-4 py-8 text-center">
          <Activity className="size-5 text-text-tertiary/50" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">No organ health scores yet</p>
          <p className="max-w-xs text-xs text-text-tertiary">
            Scores appear once the digital twin has ingested the relevant labs and studies for this patient.
          </p>
        </div>
      </PanelShell>
    );
  }

  // NOTHING scored. Ten cards each saying "No data / — / Low confidence" is ten
  // repetitions of one fact, and the repetition is what made the page read as
  // broken rather than as empty. It also buries the only useful content — WHICH
  // test unlocks WHICH organ — inside ten separate boxes.
  //
  // One block instead, shaped as what a clinician would actually do with it: an
  // order-entry checklist. The overall roll-up is omitted entirely, because a
  // roll-up over nothing is not a result.
  // "Nothing to show" must mean nothing on EITHER track. This gate used to
  // read `every(isInsufficient)`, which checks the FUNCTION track alone — so a
  // patient whose heart and brain carried scored MODIFIABLE-RISK tracks (and
  // eleven stored organ summaries) still got the "No organ has enough data to
  // score yet" wall: a false statement, the scored risk hidden, and — because
  // the empty state replaces the cards — no way to open OrganDetail, where the
  // prose summaries live. Same failure class as the summary panel's bare "Not
  // scored" (#886): a decision keyed on one track silently discarding the
  // other. The drawer said "High risk · 42" while this panel said there was
  // nothing; both were reading the same rows.
  if (ordered.every((o) => isInsufficient(o) && typeof o.risk_score !== 'number')) {
    return (
      <PanelShell count={ordered.length}>
        <div className="rounded border border-dashed border-border/60 bg-surface-sunken/10 p-5">
          <div className="flex items-start gap-2">
            <Activity className="mt-0.5 size-4 shrink-0 text-text-tertiary" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">
                No organ has enough data to score yet
              </p>
              <p className="mt-0.5 text-xs text-text-tertiary">
                {ordered.length} organs are configured. Each line is the one measurement that
                would unlock that organ.
              </p>
            </div>
          </div>
          <ul className="mt-4 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {ordered.map((organ) => {
              const gap = unscorableGap(organ);
              return (
                <li
                  key={organ.organ}
                  className="flex items-baseline gap-2 border-b border-border-subtle/60 py-1.5 text-xs last:border-0"
                >
                  <span className="w-20 shrink-0 font-medium uppercase tracking-[0.06em] text-text-tertiary">
                    {ORGAN_LABEL[organ.organ]}
                  </span>
                  <span className="min-w-0 text-text-secondary">{gap ?? 'Not scored'}</span>
                </li>
              );
            })}
          </ul>
          <p className="mt-4 flex items-start gap-1.5 text-[11px] leading-relaxed text-text-tertiary">
            <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
            <span>
              Nothing here is a clinical finding. An organ with no data is an organ that was
              not measured, not one that is healthy.
            </span>
          </p>
        </div>
      </PanelShell>
    );
  }

  return (
    <PanelShell count={ordered.length}>
      {/* THE LEGEND. "Function not scored" sitting above a risk number read as
          a contradiction to every fresh pair of eyes, including the product
          owner's — twice. The two tracks answer different questions and only
          one of them needs an organ test, and that fact was documented
          everywhere except the screen it confuses. One line, once, above all
          the cards; the meters and chips carry matching tooltips. */}
      <p className="mb-3 rounded border border-border-subtle bg-surface-sunken/20 px-3 py-2 text-[11px] leading-relaxed text-text-tertiary">
        Each organ has two independent tracks: <span className="font-medium text-text-secondary">Function</span> —
        how the organ itself is doing, graded only when its anchoring test is on file — and{' '}
        <span className="font-medium text-text-secondary">Modifiable risk</span> — changeable risk factors for
        future disease of that organ, where <span className="font-medium text-text-secondary">higher is better</span>.
        Risk often scores when function cannot; a risk number under “Function not scored” is not a contradiction.
      </p>

      {/* The roll-up sits ABOVE the grid, not inside it. It is a different KIND
          of statement — worst-organ-anchored across everything scored — and at
          five organs it happened to complete a row, so nobody noticed it was
          being rendered as the sixth card. At ten it lands orphaned on a third
          row and reads as an eleventh organ, which it is not. Full width and
          set apart is what the comment on ORGAN_ORDER already claimed was
          happening ("its own reserved slot"). */}
      <div className="mb-2">
        {overall ? (
          <OrganScoreCard
            organ={overall}
            selected={selected?.organ === 'overall'}
            onSelect={(o) => setSelectedName(o.organ)}
            history={history?.overall}
          />
        ) : (
          <OverallReservedCard />
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {ordered.map((organ) => (
          <OrganScoreCard
            key={organ.organ}
            organ={organ}
            selected={selected?.organ === organ.organ}
            onSelect={(o) => setSelectedName(o.organ)}
            history={history?.[organ.organ]}
          />
        ))}
      </div>

      {/* Calibration status, at the moment of reading — not one click away.
          The staging BANDS follow published guidelines, but the score anchors
          and modifiers are an uncalibrated expert prior (see
          risk_engine/organ_health/config.py §CALIBRATION STATUS). A clinician
          scanning "81/100" must be able to see that qualifier without opening
          the drill-down; an unqualified number is the misleading part, not a
          wrong one. OrganDetail keeps its own footer, which additionally
          carries the per-organ rubric/method versions. */}
      <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-text-tertiary">
        <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
        <span>
          Scores are an expert prior, not validated for clinical use — advisory and
          non-directive. Staging bands follow published guidelines; open an organ for
          its inputs, confidence and versions.
        </span>
      </p>

      {selected && (
        <div className="mt-5 border-t border-border/40 pt-5">
          {/* The panel states the calibration note above its cards, so the
              detail suppresses its copy and keeps only the per-organ versions. */}
          <OrganDetail organ={selected} patientLabel={patientLabel} showCalibrationNote={false} />
        </div>
      )}
    </PanelShell>
  );
}
